import { Box, Icon, Text, Link } from '@nimbus-ds/components';
import { ExclamationTriangleIcon } from '@nimbus-ds/icons';
import { Trans } from 'react-i18next';
import { useResponsive } from 'commons/components';
import { useTranslationCommon } from 'commons/hooks';
import { BlockedType } from '../../enums';

interface BlockedAlertProps {
  blockedType: BlockedType;
}

export function BlockedAlert({ blockedType }: BlockedAlertProps) {
  const t = useTranslationCommon('nuvemPagoCommons.blockedAlert');
  const { isMobile } = useResponsive();

  let title;
  let description;
  let linkText;
  let linkUrl;

  switch (blockedType) {
    case BlockedType.NEGATIVE_BALANCE:
      title = t('negativeBalance.title');
      description = 'negativeBalance.message';
      linkText = t('negativeBalance.linkText');
      linkUrl = t('negativeBalance.linkUrl');
      break;
    case BlockedType.SRE:
      title = t('sre.title');
      description = 'sre.message';
      break;
    default:
      return null;
  }

  return (
    <Box
      padding={isMobile ? '3' : '6'}
      display="flex"
      flexDirection="column"
      alignItems="center"
      width="100%"
      backgroundColor={isMobile ? 'neutral-background' : undefined}
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap="4"
        width="100%"
      >
        <Box
          display="flex"
          flex="none"
          order="0"
          flexGrow="0"
          width={isMobile ? '24px' : '32px'}
          height={isMobile ? '24px' : '32px'}
          alignItems="center"
          justifyContent="center"
        >
          <Icon
            source={<ExclamationTriangleIcon size={32} />}
            color="neutral-textHigh"
          />
        </Box>
        <Box
          width="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
          flex="none"
          order="0"
          alignSelf="stretch"
          flexGrow="0"
        >
          <Text
            fontSize="highlight"
            fontWeight="bold"
            color="primary-textHigh"
            textAlign="center"
            lineHeight="highlight"
          >
            {title}
          </Text>
        </Box>
        <Box
          width="100%"
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap="2"
          flex="none"
          order="1"
          alignSelf="stretch"
          flexGrow="0"
        >
          <Text
            fontSize={isMobile ? 'base' : 'highlight'}
            fontWeight="regular"
            color="neutral-textLow"
            textAlign="center"
            lineHeight="highlight"
          >
            <Trans
              t={t}
              i18nKey={description}
              components={{ bold: <strong /> }}
            />
          </Text>
          {linkText && linkUrl && (
            <>
              <Text
                fontSize={isMobile ? 'base' : 'highlight'}
                fontWeight="regular"
                color="neutral-textLow"
                textAlign="center"
                lineHeight="highlight"
              >
                {t('negativeBalance.linkDescription')}
              </Text>
              <Link
                as="a"
                href={linkUrl}
                target="_blank"
                rel="noopener noreferrer"
                appearance="primary"
                fontSize={isMobile ? 'base' : 'highlight'}
              >
                {linkText}
              </Link>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}
